import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { makeStyles, Box, Typography, FormControlLabel, Checkbox } from '@material-ui/core';
import ProductCard from '../common/ProductCard';

const useStyles = makeStyles(theme => ({
  container: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  item: {
    marginBottom: theme.spacing(2),
    '&:last-child': {
      marginBottom: '0',
    },
  },
  text: {
    marginBottom: theme.spacing(2),
    '&:first-child': {
      marginBottom: theme.spacing(0.5),
    },
  },
  products: {
    paddingRight: theme.spacing(1),
    overflow: 'auto',
  },
  checkbox: {
    marginTop: theme.spacing(2),
    marginLeft: '0',
  },
}));

export type upgradeProduct = {
  id:number;
  credit:number;
  productPrice: number;
  title:string;
}

function SubscriptionChange(props:any) {
  const [data, setData] = useState({id:0,products:{}});
  const [override, setOverride] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [t] = useTranslation();
  const classes = useStyles();

  const { subscription, channels, products, onChange, onDisable } = props;

  const sortedProducts = useMemo(() => _.orderBy(products, 'price', 'desc'), [products]);

  const validUpgrades = useMemo(
    () =>
      _.filter(sortedProducts, product =>
        subscription.products.validUpgrades?.some((upgrade: { id: any; }) => upgrade.id === product.id)
      ),
    [sortedProducts, subscription]
  );

  const validDowngrades = useMemo(
    () =>
      _.filter(sortedProducts, product =>
        subscription.products.validDowngrades?.some((downgrade: { id: any; }) => downgrade.id === product.id)
      ),
    [sortedProducts, subscription]
  );

  const validProducts = useMemo(
    () => validUpgrades.concat(subscription.products).concat(validDowngrades),
    [subscription, validDowngrades, validUpgrades]
  );

  useEffect(() => {
    const productId = subscription.products.id;
    const channelIds = _.map(subscription.products.features?.CHCHG, feature =>
      _.toNumber(feature.paramValue)
    );

    setData({
      id: productId,
      products: {
        [productId]: {
          id: productId,
          features: channelIds.map(channelId => ({ id: channelId })),
        },
      },
    });
  }, [subscription]);

  useEffect(() => {
    onChange({
      product: (data?.products as { [key: number]: any })?.[data.id],
      override,
    });
  }, [data, onChange, override]);

  useEffect(() => {
    onDisable(disabled);
  }, [disabled, onDisable]);

  const updateSelectedProduct = (productId: number, changes = {}) => {
    if (disabled) {
      setDisabled(false);
    }
    setData((prevData:any) => ({
      id: productId,
      products: {
        ...prevData.products,
        [productId]: {
          ...(prevData.products?.[productId]
            ? prevData.products[productId]
            : {
                id: productId,
                features: [],
              }),
          ...changes,
        },
      },
    }));
  };

  const renderProducts = () =>
    validProducts.map(product => {
      const isUpgrade = validUpgrades.some(upgrade => upgrade.id === product.id);
      const isDowngrade = validDowngrades.some(downgrade => downgrade.id === product.id);
      const isSelected = product.id === data.id;
      const isLive = product.productGroup === 'LIVECHG';

      let icon;
      let color: 'green'| 'blue'| 'orange';

      if (isUpgrade) {
        icon = 'arrow_upward';
        color = 'green';
      } else if (isDowngrade) {
        icon = 'arrow_downward';
        color = 'blue';
      } else {
        icon = 'check';
        color = 'blue';
      }

      if (isLive) {
        color = 'orange';
      }

      const upgradeProduct = getUpgradeProduct(isUpgrade,subscription,product);

      let live;
      if (isLive) {
        const selectedChannels = _.defaultTo((data.products as { [key: number]: any })?.[product.id]?.features, []);
        live = {
          value: selectedChannels.map((channel:any) => channel.id),
          items: channels,
          onChange: ({ value, checked }:any) => {
            if (!checked) {
              updateSelectedProduct(product.id, {
                features: selectedChannels.filter((channel:any) => channel.id !== _.toNumber(value)),
              });
            } else {
              updateSelectedProduct(product.id, {
                features: selectedChannels.concat({ id: _.toNumber(value) }),
              });
            }
          },
        };
      }

      return (
        <Box key={product.id} className={classes.item}>
          <ProductCard
            productId={product.id}
            title={product.title}
            subtitle={product.productGroup}
            price={product.price}
            icon={icon}
            color={color}
            live={live}
            selected={isSelected}
            onProductSelected={(value:any) => updateSelectedProduct(_.toNumber(value))}
            upgradeProduct={upgradeProduct}
          />

        </Box>
      );
    });

  return (
    <Box className={classes.container}>
      <Typography variant="body1" className={classes.text}>
        {t('subscription.subscriptionChangeInfo_1')}
      </Typography>
      <Typography variant="caption" className={classes.text}>
        {`* ${t('subscription.subscriptionChangeInfo_2')}`}
      </Typography>
      <Typography variant="caption" className={classes.text}>
        {`* ${t('subscription.subscriptionChangeInfo_3')}`}
      </Typography>
      <Box className={classes.products}>{renderProducts()}</Box>
      <FormControlLabel
        className={classes.checkbox}
        control={
          <Checkbox
            id="SubscriptionChange-override"
            color="primary"
            disabled={disabled}
            onChange={event => setOverride(event.target.checked)}
          />
        }
        label={t('subscription.subscriptionChangeOverride')}
      />
    </Box>
  );
}
function getUpgradeProduct(isUpgrade:boolean, subscription:any, product:any):upgradeProduct|undefined{
  if (isUpgrade) {
    const upgradeProduct:upgradeProduct = subscription.products.validUpgrades.find(
      (          upgrade: { id: any; }) => upgrade.id === product.id
    );
    return upgradeProduct;
  }
}
SubscriptionChange.propTypes = {
  subscription: PropTypes.shape({
    id: PropTypes.number,
    type: PropTypes.string,
    status: PropTypes.string,
    startTime: PropTypes.string,
    nextPaymentTime: PropTypes.string,
    lastModified: PropTypes.string,
    expireTime: PropTypes.string,
    commitmentEndTime: PropTypes.string,
    productName: PropTypes.string,
    amount: PropTypes.number,
    duration: PropTypes.number,
    providerId: PropTypes.number,
    paymentCount: PropTypes.number,
    events: PropTypes.arrayOf(PropTypes.object),
    products: PropTypes.shape({
      id: PropTypes.number,
      price: PropTypes.number,
      productGroup: PropTypes.string,
      title: PropTypes.string,
      features: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.object)),
      validDowngrades: PropTypes.arrayOf(PropTypes.object),
      validUpgrades: PropTypes.arrayOf(PropTypes.object),
    }),
    productchange: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      duration: PropTypes.number,
      price: PropTypes.number,
      effective_date: PropTypes.string,
      orderLines: PropTypes.arrayOf(PropTypes.object),
    }),
    provider: PropTypes.shape({
      id: PropTypes.number,
      key: PropTypes.string,
      name: PropTypes.string,
      created: PropTypes.string,
      last_modified: PropTypes.string,
    }),
    partnerExtensions: PropTypes.shape({
      associateId: PropTypes.number,
      vasToken: PropTypes.string,
    }),
    paymentInfo: PropTypes.shape({
      recurringId: PropTypes.string,
      paymentType: PropTypes.string,
      cardUpdate: PropTypes.string,
      cardType: PropTypes.string,
      cardCountry: PropTypes.string,
      cardExpMonth: PropTypes.number,
      cardExpYear: PropTypes.number,
      pic: PropTypes.string,
    }),
    pendingChanges: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  channels: PropTypes.arrayOf(PropTypes.object).isRequired,
  products: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChange: PropTypes.func.isRequired,
  onDisable: PropTypes.func.isRequired,
};

export default SubscriptionChange;
